import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import MessageDetail from "Pages/Run/Components/MessageDetail/MessageDetail";
import useUserContext from "Hooks/useUserContext";
import { direction } from "Pages/Integration/Direction";
import type { MultiValue } from "react-select";
import { format } from "date-fns";
import useAlertContext from "./useAlertContext";
import useFetch from "./useFetch";

/** A hook holding a number of helper functions.
 * @returns An object with helper functions.
 * @example
 * const { function1, function2 } = useHelpers();
 */
export default function useHelpers() {
    const [userContext, onSetUserContext] = useUserContext();
    const navigate = useRef(useNavigate());

    const { newAlert } = useAlertContext();

    const { post } = useFetch();

    const abandonJob = async (id: string) => {
        const response = await post(`/api/jobqueue/abandon/${id}`);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(data);

        if (response.ok) {
            newAlert({
                color: "info",
                message: "Abandon job request sent.",
            });
        } else {
            newAlert({
                color: "danger",
                message: `Abandon job request failed.`,
            });
        }
    };

    const cancelJob = async (id: string) => {
        const response = await post(`/api/jobqueue/cancel/${id}`);
        const data = await response.json();
        console.log(data);

        if (response.ok) {
            newAlert({
                color: "info",
                message: "Cancel job request sent.",
            });
        } else {
            newAlert({
                color: "danger",
                message: `Cancel job request failed: ${data.detail}`,
            });
        }
    };

    const copyToClipboard = useCallback(
        async (value: string) => {
            try {
                navigator.clipboard.writeText(value);
                console.log(`Value copied to clipboard: ${value}`);
                newAlert({
                    message: "Value copied to clipboard",
                    color: "success",
                });
            } catch (error) {
                console.error("Failed to copy value to clipboard", error);
                newAlert({
                    message: "Failed to copy value to clipboard",
                    color: "danger",
                });
            }
        },
        [newAlert]
    );

    const getCookieValue = useCallback((cookieKey: string): string => {
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const [key, value] = cookie.split("=");
            if (key.trim() === cookieKey) {
                return value.trim();
            }
        }
        return "";
    }, []);

    const debounce = useCallback((callback: Function, delay: number) => {
        let timerId: NodeJS.Timeout;

        return (...args: any) => {
            if (timerId) {
                clearTimeout(timerId);
            }
            timerId = setTimeout(() => {
                callback(...args);
            }, delay);
        };
    }, []);

    /** Returns directions options for a select input menu */
    const directionsSelectOptions = () => {
        const pickAnOption = (
            <option key="default" value="">
                Select a direction ...{" "}
            </option>
        );

        const directions = direction
            .getDirectionsForSystems()
            .map((direction) => (
                <option key={direction} value={direction}>
                    {direction[0].toUpperCase() + direction.slice(1)}
                </option>
            ));

        return (
            <>
                {pickAnOption}
                {directions}
            </>
        );
    };

    /** Format seconds into hours, minutes, seconds */
    const formatSeconds = useCallback((seconds: number) => {
        let result = "";

        const run_hours = Math.floor(seconds / (60 * 60));
        if (run_hours > 0) {
            result += `${run_hours}h `;
        }

        const run_minutes = Math.floor((seconds - run_hours * 60 * 60) / 60);
        if (run_minutes > 0) {
            result += `${run_minutes}m `;
        }

        const secondsLeft = Math.floor(
            seconds - run_hours * 60 * 60 - run_minutes * 60
        );

        if (secondsLeft > 0 || result === "") {
            result += `${secondsLeft}s`;
        }

        return result;
    }, []);

    /** Generic onChange callback for React controlled inputs.
     * @param setFormData State setter function. Sets the form data object,
     * whose key is the name of the input and whose value is input value.
     */
    const handleFormChange = useCallback(
        (
            e:
                | React.ChangeEvent<HTMLInputElement>
                | React.ChangeEvent<HTMLTextAreaElement>,
            setFormData: Function
        ): void => {
            const name = e.target.name;
            let value: boolean | string = e.target.value;

            if (
                e.target instanceof HTMLInputElement &&
                e.target.type === "checkbox"
            ) {
                value = e.target.checked;
            }

            setFormData((formData: any) => ({ ...formData, [name]: value }));
        },
        []
    );

    /**Sets special permission fields in react-select inputs */
    const handlePermissions = useCallback(
        (
            e: MultiValue<{ value: string; label: string }>,
            setPermissions: Function,
            field: string
        ): void => {
            if (field === "Role:" && e != null)
                setPermissions((permissions: any) => ({
                    ...permissions,
                    role: e.map((r) => r.value).join(","),
                }));
            else if (field === "Process:" && e != null)
                setPermissions((permissions: any) => ({
                    ...permissions,
                    process: e.map((r) => r.value).join(","),
                }));
            else if (field === "Review:" && e != null)
                setPermissions((permissions: any) => ({
                    ...permissions,
                    review: e.map((r) => r.value).join(","),
                }));
        },
        []
    );

    const isValidEmail = useCallback((email: string): boolean => {
        const emailRegex =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }, []);

    /** Sets the user context. This is only a helper function. */
    const updateUserContext = useCallback(
        (data: any) => {
            const newUserContext = {
                companies: Number.parseInt(data.companies),
                companyId: data.company_id,
                companyName: data.company_name,
                companyStatus: data.company_status,
                companySystems: data.company_systems,
                environment: data.environment,
                isAuthenticated: true,
                isCalanceAdmin: data.is_calance_admin,
                landingPage: data.landing_page,
                permissions:
                    typeof data.permissions === "string"
                        ? JSON.parse(data.permissions)
                        : data.permissions,
                preferences: data.preferences ?? {},
                statusMessages:
                    typeof data.status_messages === "string"
                        ? JSON.parse(data.status_messages)
                        : data.status_messages,
                userId: data.user_id,
                username: data.username,
            };

            console.log("User context updated: ", newUserContext);

            onSetUserContext(newUserContext);
        },
        [onSetUserContext]
    );

    /** Sets the user context with all the login data, stores the JWT in a cookie, 
    and then navigates user to the landing page */
    const handleLoginCompanyOkResponse = useCallback(
        (data: any): void => {
            updateUserContext(data);
            if (userContext.isCalanceAdmin === "1") {
                const lastPage = localStorage.getItem("lastPage");
                navigate.current(lastPage ?? data.landingPage);
            } else {
                navigate.current(data.landing_page);
            }
        },
        [updateUserContext, userContext.isCalanceAdmin]
    );

    const handleMeEndpointOkResponse = useCallback(
        (data: any): void => {
            updateUserContext(data);
        },
        [updateUserContext]
    );

    const isCalanceAdmin = useCallback((): boolean => {
        return Boolean(userContext.isCalanceAdmin === "1");
    }, [userContext.isCalanceAdmin]);

    const isAdmin = useCallback((): boolean => {
        let admin = false;

        if (userContext.permissions.Admin !== undefined)
            admin = userContext.permissions.Admin;

        // isCalanceAdmin is stored as 0 | 1 in DB
        return isCalanceAdmin() || admin;
    }, [isCalanceAdmin, userContext.permissions.Admin]);

    const canRerunAll = useCallback(
        (initiator: string): boolean => {
            if (isAdmin()) {
                return true;
            }

            const canRerunAll =
                userContext.permissions.Process?.toLowerCase().includes(
                    "rerunall"
                ) ||
                userContext.userId.toLowerCase() === initiator.toLowerCase();

            return canRerunAll;
        },
        [isAdmin, userContext]
    );

    //** Only a Calance admin can abandon, anyone can cancel */
    const canAbandonJobs = useCallback(() => {
        return isCalanceAdmin();
    }, [isCalanceAdmin]);

    const canEditSchedule = useCallback(() => {
        if (isAdmin()) {
            return true;
        }

        const canEditSchedule = Boolean(userContext.permissions.EditSchedule);

        return canEditSchedule;
    }, [isAdmin, userContext.permissions.EditSchedule]);

    /** Formats the message details text (line breaks, making links urls, and
    putting details inside a collapsible card and pre tag). Also used in RunDetails.tsx */
    const formatMessageText = useCallback(
        (textIn: string, showAllDetails = true): string | JSX.Element => {
            const detailsTag = "[[DETAILS]]";
            const detailsStart = textIn.indexOf(detailsTag);

            // If there is a [[DETAILS]] tag, the text should have a collapsible <pre> section
            if (detailsStart > -1) {
                // Separate into firstPart and details sections. Also format any duration values.
                const firstPart = formatDuration(
                    textIn.substring(0, detailsStart)
                );
                const details = formatDuration(
                    textIn.substring(detailsStart + detailsTag.length)
                );

                // Convert links to HTML tags in the first part and details
                const firstPartWithLinks = convertLinks(firstPart);
                const detailsWithLinks = convertLinks(details);

                return (
                    <MessageDetail
                        showAllDetails={showAllDetails}
                        firstPart={firstPartWithLinks}
                        details={detailsWithLinks}
                    />
                );
            }

            // If no [[DETAILS]] tag, format the text
            const newText = formatDuration(textIn);
            const textWithLinks = convertLinks(newText);
            return (
                <div
                    style={{ whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{
                        __html: textWithLinks,
                    }}
                />
            );

            // Helper function to convert links in a string to HTML anchors
            function convertLinks(text: string = ""): string {
                // Identify a URL in the line as a string starting with 'http:' or 'https:',
                // followed by some non - zero number of letters, numbers, or URL - permitted punctuation characters,
                // which may or may not be enclosed in parentheses.
                // If the URL is preceded by text enclosed in square brackets, identify it as the 'name' of the intended link
                const arrayOfLines = text.split("\n");
                const linkRegex =
                    /(?:\[(?<name>.+?)\])?\(?(?<url>https?:\/\/[-._~:/%?#[\]@!$&'()*+,;=\w]+\b)\/?\)?/;

                const newArrayOfLines = arrayOfLines.map((line) => {
                    const match = line.match(linkRegex);
                    if (
                        match !== null &&
                        match.groups !== undefined &&
                        match.groups.url !== undefined
                    ) {
                        const url = match.groups.url;
                        const name = match.groups.name;

                        // If this is a link to add an integration to the job queue, don't make a real link
                        let link = "";
                        if (
                            url
                                ?.toLowerCase()
                                .includes(
                                    "dimension.calance.us/api/jobqueue"
                                ) ||
                            name?.toLowerCase().includes("click here")
                        ) {
                            link = `<a href='#' id='addToJobQueueLink' data-url="${url}">${
                                name ?? url
                            }</a>`;
                        } else {
                            // Replace the matched url or markdown link with an <a> tag, but leave the rest of the line alone
                            link = `<a href='${url}' target="_blank" rel="noopener noreferrer">${
                                name ?? url
                            }</a>`;
                        }
                        const result = line.replace(match[0], link);
                        // Enclose the entire line in a <span> to prevent line breaking
                        return `<span>${result.trimStart()}</span>`;
                    }
                    const lineWithPreservedXmlIfAny = line
                        .replace(/</g, "&lt;")
                        .replace(/>/g, "&gt;");
                    return lineWithPreservedXmlIfAny;
                });
                const result = newArrayOfLines.join("\n");

                return result;
            }

            // Helper function to better format stranger durations like 0d 0h 5m 248ms
            function formatDuration(str: string) {
                // Match a duration value like 0d 0h 5m 287ms
                let timePattern = /(\d+d )?(\d+h )?(\d+m )?(\d+ms)?/g;

                let formattedStr = str.replace(timePattern, function (match) {
                    // Every group in timePattern is optional, so the pattern also
                    // matches the EMPTY string at every position — the callback fires
                    // once per character (measured: 9,250 calls for 9,225 chars on a
                    // real 25-row run_details page). An empty match always produced ""
                    // below, so returning early is output-identical and skips the
                    // split/array work: 2.22 ms -> 1.34 ms per 25-row page.
                    // Verified identical over 300,036 inputs (36 hand cases + 300k
                    // randomized) before landing. Do NOT "fix" this by anchoring the
                    // regex so it cannot match empty — the surrounding behaviour
                    // depends on the empty-match semantics and anchoring changes real
                    // output (e.g. a bare "248ms" is currently deleted, and
                    // "took 1d 2h" currently renders "took 1d2h"). See bead
                    // dimension-vy63.
                    if (match === "") {
                        return match;
                    }
                    let timeUnits = match.split(" ");
                    let formattedUnits: any = [];

                    // Remove any leading 0s
                    for (let i = 0; i < timeUnits.length; i++) {
                        if (
                            timeUnits[i] !== "0d" &&
                            timeUnits[i] !== "0h" &&
                            timeUnits[i] !== "0m" &&
                            timeUnits[i] !== ""
                        ) {
                            formattedUnits.push(timeUnits[i]);
                        }
                    }
                    // Remove trailing millisecond values
                    if (
                        formattedUnits.length > 0 &&
                        formattedUnits[formattedUnits.length - 1].endsWith("ms")
                    ) {
                        formattedUnits.pop();
                    }
                    return formattedUnits.join(" ");
                });
                return formattedStr;
            }
        },
        []
    );

    const formatIsoStrings = useCallback((text: string): string => {
        const isoRegex =
            /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)(T(0[0-9]|1[0-9]|2[0-4])(:(0[0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])(:(0[0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9]))?(\.\d+)?)?(Z|([+-])(0[0-9]|1[0-9]|2[0-4])(:(0[0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9]))?)?)?$/gim;
        const newText = text.replace(isoRegex, (match) => {
            return format(new Date(match), "MMM d, yyyy, HH:mm");
        });
        return newText;
    }, []);

    const replacePlussesWithSpaces = useCallback((textIn: string): string => {
        // replace plus signs surrounded by characters with spaces, and replace any remaining (beginning or end of string) with nothing.
        return textIn
            .replace(/(?<=\w)\+(?=\w)/g, " ")
            .replace("+", "")
            .trim();
    }, []);

    return {
        abandonJob,
        cancelJob,
        isAdmin,
        isCalanceAdmin,
        canRerunAll,
        canAbandonJobs,
        canEditSchedule,
        copyToClipboard,
        debounce,
        directionsSelectOptions,
        getCookieValue,
        formatMessageText,
        formatSeconds,
        formatIsoStrings,
        handleFormChange,
        handlePermissions,
        handleLoginCompanyOkResponse,
        handleMeEndpointOkResponse,
        isValidEmail,
        replacePlussesWithSpaces,
    };
}
